import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ChevronLeft, ChevronRight, Check, Shield, User as UserIcon, KeyRound, ListChecks, Info } from "lucide-react";
import { usePermissionTemplates } from "@/lib/queries";
import { useCreateUser } from "@/lib/mutations";
import { DEFAULT_USER_GRANTS } from "@emr/shared";
import { PermissionMatrix } from "./PermissionMatrix";
import { TemplateSelector } from "./TemplateSelector";

const STEPS = ["Identity", "Template", "Permissions"] as const;

export function CreateUserDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const [step, setStep] = useState(0);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [role, setRole] = useState<"admin" | "user">("user");
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set(DEFAULT_USER_GRANTS));
  const [pendingTemplateApply, setPendingTemplateApply] = useState<{ id: string | null; ids: string[] } | null>(null);

  const { data: templatesData } = usePermissionTemplates();
  const createUser = useCreateUser();

  const templates = templatesData?.templates ?? [];
  const isAdmin = role === "admin";

  const reset = () => {
    setStep(0);
    setFullName("");
    setEmail("");
    setPassword("");
    setConfirm("");
    setRole("user");
    setTemplateId(null);
    setSelectedIds(new Set(DEFAULT_USER_GRANTS));
    setPendingTemplateApply(null);
  };

  const close = () => { reset(); onOpenChange(false); };

  const identityValid = fullName.trim().length > 0 && /\S+@\S+\.\S+/.test(email) && password.length >= 8 && password === confirm;

  const applyTemplate = (id: string | null, rawIds: string[]) => {
    // "No template" means the DEFAULT grants (matching the info alert in
    // step 1) — NOT an empty selection. Previously it silently wiped the
    // selection to zero grants.
    const ids = id === null ? DEFAULT_USER_GRANTS : rawIds;
    const same = ids.length === selectedIds.size && ids.every((k) => selectedIds.has(k));
    if (!same && selectedIds.size > 0) {
      setPendingTemplateApply({ id, ids });
      return;
    }
    commitTemplate(id, ids);
  };

  const commitTemplate = (id: string | null, ids: string[]) => {
    setTemplateId(id);
    setSelectedIds(new Set(ids));
    setPendingTemplateApply(null);
  };

  const submit = async () => {
    if (!identityValid) return;
    try {
      // Single write: permissionIds ride along in the create body — the
      // server resolves + grants them transactionally (previously this also
      // POSTed /permissions again: a second round trip, duplicate audit
      // row, and a double toast). The success toast comes from the
      // mutation's onSuccess in mutations.ts — don't duplicate it here.
      await createUser.mutateAsync({
        email, password, fullName, role,
        permissionIds: isAdmin ? [] : Array.from(selectedIds),
      });
      close();
    } catch { /* toast handled */ }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); else onOpenChange(v); }}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-maroon-700 dark:text-amber-500">Add Person</DialogTitle>
          <DialogDescription>Create a new user with identity, template, and permissions.</DialogDescription>
        </DialogHeader>

        <Stepper currentStep={isAdmin ? 0 : step} total={isAdmin ? 1 : 3} labels={isAdmin ? ["Identity"] : STEPS} />

        {isAdmin && (
          <Alert>
            <Shield className="h-4 w-4" />
            <AlertDescription>
              Admin users have full implicit access to every module. No template or permission selection is required.
            </AlertDescription>
          </Alert>
        )}

        {step === 0 && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <Label>Full name *</Label>
              <Input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Dr. Jane Doe" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@clinic.local" />
            </div>
            <div>
              <Label>Password * <span className="text-xs text-muted">(min 8 chars)</span></Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
              {password.length > 0 && password.length < 8 && (
                <p className="mt-1 text-xs text-red-600">Password must be at least 8 characters.</p>
              )}
            </div>
            <div>
              <Label>Confirm password *</Label>
              <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
              {confirm.length > 0 && password !== confirm && (
                <p className="mt-1 text-xs text-red-600">Passwords do not match.</p>
              )}
            </div>
            <div>
              <Label>Role</Label>
              <Select value={role} onValueChange={(v) => setRole(v as "admin" | "user")}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User (granular permissions)</SelectItem>
                  <SelectItem value="admin">Admin (full access)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {!isAdmin && step === 1 && (
          <div className="space-y-3">
            <TemplateSelector
              templates={templates}
              value={templateId ?? "none"}
              onChange={(id, ids) => applyTemplate(id, ids)}
            />
            {templateId === null && (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  Without a template, the default grants ({DEFAULT_USER_GRANTS.length} permissions) are applied. You can tweak them in the next step.
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {!isAdmin && step === 2 && (
          <PermissionMatrix selectedIds={selectedIds} onChange={setSelectedIds} />
        )}

        {pendingTemplateApply && (
          <Dialog open={!!pendingTemplateApply} onOpenChange={(v) => { if (!v) setPendingTemplateApply(null); }}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Replace current permissions?</DialogTitle>
                <DialogDescription>
                  You currently have {selectedIds.size} permission(s) selected. Applying the template will replace them with {pendingTemplateApply.ids.length} permission(s). Continue?
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setPendingTemplateApply(null)}>Cancel</Button>
                <Button className="bg-maroon-600 hover:bg-maroon-700 text-cream-50" onClick={() => commitTemplate(pendingTemplateApply.id, pendingTemplateApply.ids)}>
                  Replace
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        <DialogFooter className="justify-between">
          <Button variant="ghost" onClick={close}>Cancel</Button>
          <div className="flex gap-2">
            {step > 0 && !isAdmin && (
              <Button variant="outline" onClick={() => setStep(step - 1)}>
                <ChevronLeft size={14} className="mr-1" /> Back
              </Button>
            )}
            {!isAdmin && step < 2 && (
              <Button onClick={() => setStep(step + 1)} disabled={step === 0 && !identityValid} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">
                Next <ChevronRight size={14} className="ml-1" />
              </Button>
            )}
            {(isAdmin || step === 2) && (
              <Button onClick={submit} disabled={!identityValid || createUser.isPending} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">
                <Check size={14} className="mr-1" /> Create
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stepper({ currentStep, total, labels }: { currentStep: number; total: number; labels: readonly string[] }) {
  const icons = [UserIcon, KeyRound, ListChecks, Shield];
  return (
    <div className="flex items-center justify-between gap-1 rounded-lg border border-cream-200 bg-cream-50 p-3">
      {Array.from({ length: total }).map((_, i) => {
        const Icon = icons[i] ?? UserIcon;
        const active = i === currentStep;
        const done = i < currentStep;
        return (
          <div key={i} className="flex flex-1 items-center">
            <div className={`flex items-center gap-2 ${active ? "text-maroon-700" : done ? "text-green-700" : "text-muted"}`}>
              <div className={`flex h-8 w-8 items-center justify-center rounded-full border ${active ? "border-maroon-600 bg-maroon-600 text-cream-50" : done ? "border-green-700 bg-green-700 text-cream-50" : "border-cream-200 bg-white"}`}>
                <Icon size={14} />
              </div>
              <div className="text-sm font-medium">{labels[i]}</div>
            </div>
            {i < total - 1 && <div className={`mx-2 h-px flex-1 ${done ? "bg-green-700" : "bg-cream-200"}`} />}
          </div>
        );
      })}
    </div>
  );
}

export { STEPS as CREATE_USER_STEPS };
